import { useFieldArray, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { handleOnSettled, validateFloat } from '@utils/formHelpers';
import { DefaultApiResp, MaterialOption } from '@root/globalTypes';
import { useForemanUpdateMaterial } from '@services/materials/foreman/update';
import { useUpdateMaterial } from '@services/materials/update';
import { useErrors } from '@hooks/errors';
import { MaterialUpdateFormProps, MaterialUpdateFormValues } from './types';
import { FormProps } from '@forms/types';
import { defaultValues } from './constants';
import * as z from 'zod';
import { useTranslation } from 'react-i18next';

export const useMaterialUpdateForm = ({
  successHandler,
  material,
  setIsVisible,
  foreman,
}: FormProps<MaterialUpdateFormProps>) => {
  const handleErrors = useErrors();
  const { t } = useTranslation('app');

  const formSchema = z
    .object({
      final: z.boolean(),
      optionsAttributes: z.array(
        z.object({
          id: z.string(),
          name: z.string(),
          unit: z.string(),
          finalQuantity: z.string(),
          finalQuantityRepeat: z.string(),
          valueType: z.string(),
          required: z.boolean(),
          doubleEntryRequired: z.boolean(),
        }),
      ),
    })
    .superRefine(({ optionsAttributes }, ctx) => {
      optionsAttributes.forEach(
        ({ valueType, finalQuantity, finalQuantityRepeat, required, doubleEntryRequired }, i) => {
          if (!required || !foreman) return;
          if (valueType === 'text' ? !finalQuantity : !validateFloat(finalQuantity)) {
            ctx.addIssue({
              code: 'too_small',
              minimum: 1,
              inclusive: false,
              type: 'string',
              path: [`optionsAttributes[${i}].finalQuantity`],
              message: t(`forms.final.required.${valueType}`),
            });
          }
          if (
            doubleEntryRequired &&
            (valueType === 'text'
              ? finalQuantity !== finalQuantityRepeat
              : Number(finalQuantity) !== Number(finalQuantityRepeat))
          ) {
            ctx.addIssue({
              code: 'too_small',
              minimum: 1,
              inclusive: false,
              type: 'string',
              path: [`optionsAttributes[${i}].finalQuantityRepeat`],
              message: t('repeatFinal.required'),
            });
          }
        },
      );
    });

  const form = useForm<MaterialUpdateFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues,
  });

  const { control, setError } = form;

  const fieldArray = useFieldArray({ control, name: 'optionsAttributes' });

  const callback = (resp: DefaultApiResp) => {
    handleOnSettled<DefaultApiResp>(resp, successHandler, () =>
      handleErrors(resp.errors, (key, message) =>
        setError(key as keyof MaterialUpdateFormValues, { message }),
      ),
    );
  };

  const foremanUpdate = useForemanUpdateMaterial({ callback });
  const materialUpdate = useUpdateMaterial({ material, callback });

  const onSubmit = async ({ optionsAttributes }: MaterialUpdateFormValues) => {
    if (!material) return;
    const { id, jobId, tenantId } = material;

    const parsedValues = {
      id,
      jobId,
      tenantId,
      optionsAttributes: optionsAttributes?.map((option: MaterialOption) => ({
        id: option.id || '',
        finalQuantity:
          option.valueType === 'number'
            ? parseFloat(option.finalQuantity || '')
            : option.finalQuantity,
      })),
    };

    if (foreman) foremanUpdate.mutate(parsedValues);
    else materialUpdate.mutate(parsedValues);
    if (setIsVisible) setIsVisible(false);
  };

  return {
    form,
    fieldArray,
    onSubmit,
  };
};
